// Avatar — access-review initials disc with optional photo.
// Ported from the hand-rolled avatar in
// /Users/harry/projects/ploy/client/.worktrees/access-review-improvements/src/designs/employee-portal-access-reviews/design.tsx.

import { Box, Image, type BoxProps } from "@chakra-ui/react";
import { forwardRef, useEffect, useState } from "react";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

export interface AvatarProps extends Omit<
  BoxProps,
  "as" | "children" | "ring" | "size"
> {
  /** Used to derive initials (first letters of the first two name words). */
  name: string;
  /** Optional photo URL — when present, replaces initials. */
  src?: string;
  /** Deprecated compatibility prop. The access-review avatar always uses azurite. */
  tone?: string;
  /** Avatar size. Default `md` (32×32). */
  size?: AvatarSize;
  /** Deprecated compatibility prop. The avatar always renders its inset ring. */
  ring?: boolean;
}

// Access-review design sizes. xs/xl remain as compatibility aliases for older
// DS call sites; the campaign surfaces use sm/md/lg.
const sizeMap: Record<AvatarSize, { px: number; fontSize: string }> = {
  xs: { px: 20, fontSize: "10px" },
  sm: { px: 24, fontSize: "11px" },
  md: { px: 32, fontSize: "11px" },
  lg: { px: 48, fontSize: "16px" },
  xl: { px: 56, fontSize: "18px" },
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0][0]!.toUpperCase();
  return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
}

const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  ({ name, src, tone: _tone, size = "md", ring: _ring, ...boxProps }, ref) => {
    const { px, fontSize } = sizeMap[size];
    const [imgFailed, setImgFailed] = useState(false);
    const showImage = Boolean(src) && !imgFailed;

    useEffect(() => {
      setImgFailed(false);
    }, [src]);

    return (
      <Box
        as="span"
        ref={ref}
        {...boxProps}
        position="relative"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        overflow="hidden"
        width={`${px}px`}
        height={`${px}px`}
        borderRadius="50%"
        bg="bg.azurite"
        color="p.700"
        fontFamily="body"
        fontWeight={600}
        fontSize={fontSize}
        lineHeight="1"
        // The ring is drawn INSIDE the disc (inset shadow), not as a border:
        // a border painted a hard white outline around the silhouette.
        boxShadow="avatar-ring"
      >
        {showImage ? (
          <Image
            src={src}
            alt={name}
            width="100%"
            height="100%"
            borderRadius="50%"
            objectFit="cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          initials(name) || "?"
        )}
      </Box>
    );
  },
);

Avatar.displayName = "Avatar";

export default Avatar;
